import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import {
  Archive,
  Eye,
  PencilLine,
  Plus,
  Search,
  ShieldAlert,
  Wallet,
  X,
} from "lucide-react";
import Panel from "../../components/Panel";
import { EmptyState, PageHeader, Pagination } from "../../components/erp";
import { financeAPI } from "../../services/businessErpService";
import { Button, ConfirmDialog, Dialog, Notice } from "../../../components/ui";
import { businessApi } from "../../../services/api";
import DatePickerInput from "../../../components/DatePickerInput";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import { formatCents, formatDate, titleCase } from "./financeUtils";
import ShopSelect from "../../../components/ShopSelect";
import { SALARY_COVERAGE_OPTIONS, deriveRatePreview } from "../../../utils/compensationMath";
import { NO_FIXED_ALLOWANCE_TEXT, supportsFixedAllowance } from "../hr/compensationView";

const TODAY = new Date().toISOString().slice(0, 10);
const SALARY_ROWS_PER_PAGE = 6;

const BLANK_FORM = {
  templateName: "",
  businessRoleId: "",
  employmentType: "full_time",
  salaryFrequency: "monthly",
  salaryDayCoverage: "",
  baseSalary: "",
  allowance: "",
  effectiveFrom: TODAY,
  effectiveUntil: "",
  isDefaultForRole: true,
  isActive: true,
  notes: "",
};

function centsToInput(value) {
  if (value === undefined || value === null || value === "") return "";
  return String(Number(value) / 100);
}

function inputToCents(value) {
  if (value === undefined || value === null || value === "") return 0;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : 0;
}

function formatOptionalCents(value) {
  if (value === undefined || value === null || value === "") return "-";
  return formatCents(value);
}

function normalizeTemplatesResponse(response) {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

function normalizeRolesResponse(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

function normalizeEmploymentTypesResponse(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

function compensationRequirementLabel() {
  // Report 60 Stage 1 / Package A0: no business work week converts pay; a salary's daily rate belongs to each employee's pay record.
  return "The base amount is the pay for the chosen frequency (per month, per cutoff, per day or per hour). A daily or hourly amount is its own rate; a salary's daily rate is set on each employee's pay record.";
}

function cleanActionError(error, fallback) {
  const raw = String(error?.message || "").trim();
  if (!raw) return fallback;
  const normalized = raw
    .replace(/^Request failed:\s*\d+\s*[^\w\s]?\s*/i, "")
    .replace(/^Request failed:\s*/i, "");
  if (/baseSalaryCents/i.test(normalized)) return "Base salary is required and must be greater than zero.";
  if (/already exists for this role/i.test(normalized)) return "A pay preset already exists for this role, employment type, frequency and start date.";
  return normalized || raw || fallback;
}

function TemplateModal({ open, initial, roles, employmentTypes, onClose, onSubmit }) {
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (!initial) {
      setForm(BLANK_FORM);
      setError("");
      setSaving(false);
      return;
    }
    setForm({
      templateName:
        initial.template_name
        || initial.templateName
        || `${initial.business_role_display_name || initial.business_role_name || "Business Role"} Template`,
      businessRoleId: String(initial.business_role_id || initial.businessRoleId || ""),
      employmentType: initial.employment_type || initial.employmentType || "full_time",
      salaryFrequency: initial.salary_frequency || initial.salaryFrequency || "monthly",
      salaryDayCoverage: initial.salary_day_coverage || initial.salaryDayCoverage || "",
      baseSalary: centsToInput(initial.base_salary_cents ?? initial.baseSalaryCents),
      allowance: centsToInput(initial.allowance_cents ?? initial.allowanceCents),
      effectiveFrom: String(initial.effective_from || initial.effectiveFrom || "").slice(0, 10) || TODAY,
      effectiveUntil: String(initial.effective_until || initial.effectiveUntil || "").slice(0, 10),
      isDefaultForRole: Boolean(initial.is_default_for_role ?? initial.isDefaultForRole),
      isActive: Boolean(initial.is_active ?? initial.isActive),
      notes: initial.notes || "",
    });
    setError("");
    setSaving(false);
  }, [open, initial]);

  const employmentTypeOptions = employmentTypes.length
    ? employmentTypes
    : ["full_time", "part_time", "contract", "probationary", "intern"].map((value) => ({
      value,
      label: titleCase(value),
    }));

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const editing = Boolean(initial?.id);
  // Policy A: a preset saved before it may still suggest "every day of the month". It is kept until changed (hiring
  // ignores it); it is never offered for a new suggestion.
  const olderEveryDay = (initial?.salary_day_coverage || initial?.salaryDayCoverage) === "EVERY_DAY";
  const derivedRates = useMemo(
    // Package A0: a preset states no agreed daily rate, so a salaried preset shows none; daily / hourly presets
    // convert over 8 normal hours only when full-time.
    () => deriveRatePreview(form.baseSalary, form.salaryFrequency, { employmentType: form.employmentType, salaryDayCoverage: form.salaryDayCoverage }),
    [form.baseSalary, form.salaryFrequency, form.employmentType, form.salaryDayCoverage],
  );

  if (!open) return null;

  async function handleSubmit() {
    setError("");
    if (!form.templateName.trim()) {
      setError("Preset name is required.");
      return;
    }
    if (form.templateName.trim().length > 150) {
      setError("Preset name must be 150 characters or fewer.");
      return;
    }
    if (!form.businessRoleId) {
      setError("Business role is required.");
      return;
    }
    if (!form.effectiveFrom) {
      setError("Effective date is required.");
      return;
    }
    if (form.effectiveUntil && form.effectiveUntil < form.effectiveFrom) {
      setError("Effective end date cannot be earlier than the effective date.");
      return;
    }
    if (!Number.isFinite(Number(form.baseSalary)) || Number(form.baseSalary) <= 0) {
      setError("Base salary is required and must be greater than zero.");
      return;
    }
    if (supportsFixedAllowance(form.salaryFrequency) && (!Number.isFinite(Number(form.allowance || 0)) || Number(form.allowance || 0) < 0)) {
      setError("Allowance must be zero or greater.");
      return;
    }
    if (String(form.notes || "").trim().length > 1000) {
      setError("Notes must be 1000 characters or fewer.");
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        templateName: form.templateName.trim(),
        businessRoleId: Number(form.businessRoleId),
        employmentType: form.employmentType,
        salaryFrequency: form.salaryFrequency,
        baseSalaryCents: inputToCents(form.baseSalary),
        // Daily and hourly presets carry no fixed allowance, whatever was typed before the frequency changed.
        allowanceCents: supportsFixedAllowance(form.salaryFrequency) ? inputToCents(form.allowance) : 0,
        // Package A0: a suggestion of what the salary pays for (optional); the server derives any rates itself.
        // An older "every day" suggestion left as it is is not sent, so the server keeps it rather than refusing the edit.
        ...(supportsFixedAllowance(form.salaryFrequency) && form.salaryDayCoverage === "EVERY_DAY" ? {}
          : { salaryDayCoverage: supportsFixedAllowance(form.salaryFrequency) ? (form.salaryDayCoverage || null) : null }),
        effectiveFrom: form.effectiveFrom,
        effectiveUntil: form.effectiveUntil || null,
        isDefaultForRole: Boolean(form.isDefaultForRole),
        isActive: Boolean(form.isActive),
        notes: form.notes.trim() || null,
      });
    } catch (err) {
      setError(cleanActionError(err, "The pay preset could not be saved."));
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => { if (!next && !saving) onClose(); }}
      title={editing ? "Edit pay preset" : "New pay preset"}
      description="A starting amount for a business role. It prefills the pay form when HR hires someone into the role; HR always confirms the actual pay."
      size="wide"
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={handleSubmit}>
            {editing ? "Save preset" : "Create preset"}
          </Button>
        </>
      )}
    >

        {error ? (
          <Notice tone="danger" className="mb-4">{error}</Notice>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Preset name</label>
            <input maxLength={150} value={form.templateName} onChange={(event) => setField("templateName", event.target.value)} className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" placeholder="e.g. HR Manager Default Salary" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Business Role</label>
            <ShopSelect value={form.businessRoleId} onValueChange={(value) => setField("businessRoleId", value)} options={[{ value: "", label: "Select business role" }, ...roles.map((role) => ({ value: role.id, label: role.displayName || role.display_name || role.name }))]} ariaLabel="Business Role" align="start" minPanelWidth={240} triggerClassName="min-h-10 w-full rounded-2xl px-3 py-2.5 text-sm" panelClassName="py-1" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Employment Type</label>
            <ShopSelect value={form.employmentType} onValueChange={(value) => setField("employmentType", value)} options={employmentTypeOptions.map((type) => ({ value: type.value || type, label: type.label || titleCase(type.value || type) }))} ariaLabel="Employment Type" align="start" minPanelWidth={220} triggerClassName="min-h-10 w-full rounded-2xl px-3 py-2.5 text-sm" panelClassName="py-1" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Salary basis</label>
            <ShopSelect value={form.salaryFrequency} onValueChange={(value) => setField("salaryFrequency", value)} options={["monthly", "semi_monthly", "bi_weekly", "weekly", "daily", "hourly"].map((value) => ({ value, label: titleCase(value) }))} ariaLabel="Salary basis" align="start" minPanelWidth={220} triggerClassName="min-h-10 w-full rounded-2xl px-3 py-2.5 text-sm" panelClassName="py-1" />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">How the amount is stated, not how often it is paid (that is the pay schedule). {compensationRequirementLabel()}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Base Salary</label>
            <input type="number" min="0" step="0.01" value={form.baseSalary} onChange={(event) => setField("baseSalary", event.target.value)} className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Allowance</label>
            {supportsFixedAllowance(form.salaryFrequency) ? (
              <input type="number" min="0" step="0.01" aria-label="Allowance" value={form.allowance} onChange={(event) => setField("allowance", event.target.value)} className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" />
            ) : (
              <p className="rounded-2xl border border-dashed border-neutral-300 px-3 py-2.5 text-sm text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">{NO_FIXED_ALLOWANCE_TEXT}</p>
            )}
          </div>
          {/* Policy A: new salaries always pay the scheduled work days, so a new preset has no coverage to suggest. The
              choice is shown only to change an older preset that still suggests "every day". */}
          {supportsFixedAllowance(form.salaryFrequency) && olderEveryDay ? (
            <div>
              <label htmlFor="preset-salary-coverage" className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Salary usually pays for</label>
              <select id="preset-salary-coverage" value={form.salaryDayCoverage} onChange={(event) => setField("salaryDayCoverage", event.target.value)} className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900">
                <option value="">No suggestion</option>
                {SALARY_COVERAGE_OPTIONS.filter((option) => option.value !== "EVERY_DAY" || olderEveryDay).map((option) => <option key={option.value} value={option.value} disabled={option.value === "EVERY_DAY"}>{option.label}</option>)}
              </select>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{form.salaryDayCoverage === "EVERY_DAY"
                ? "New hires no longer use \"every day of the month\", so hiring ignores this suggestion. Choose another one when you next edit this preset."
                : "A suggestion for hiring only. HR confirms it on each new employee's pay."}</p>
            </div>
          ) : null}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Daily Rate</label>
            <input type="text" readOnly value={derivedRates.dailyRateCents ? centsToInput(derivedRates.dailyRateCents) : ""} className="w-full rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900/70 dark:text-neutral-300" />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{supportsFixedAllowance(form.salaryFrequency) ? "Not part of a preset: a salary's daily rate is set on each employee's pay record." : form.salaryFrequency === "daily" ? "The daily amount itself." : derivedRates.workHoursPerDay ? `${derivedRates.workHoursPerDay} normal hours a day.` : "Not converted: part-time has no set normal day."}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Hourly Rate</label>
            <input type="text" readOnly value={derivedRates.hourlyRateCents ? centsToInput(derivedRates.hourlyRateCents) : ""} className="w-full rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900/70 dark:text-neutral-300" />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{supportsFixedAllowance(form.salaryFrequency) ? "Not part of a preset." : form.salaryFrequency === "hourly" ? "The hourly amount itself." : derivedRates.workHoursPerDay ? `Over ${derivedRates.workHoursPerDay} normal hours a day.` : "Not converted: part-time has no set normal day."}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Effective From</label>
            <DatePickerInput value={form.effectiveFrom} onValueChange={(value) => setField("effectiveFrom", value)} title="SELECT EFFECTIVE DATE" ariaLabel="Effective from" className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Effective Until</label>
            <DatePickerInput min={form.effectiveFrom || undefined} value={form.effectiveUntil} onValueChange={(value) => setField("effectiveUntil", value)} title="SELECT END DATE" ariaLabel="Effective until" className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Notes</label>
            <textarea maxLength={1000} rows={4} value={form.notes} onChange={(event) => setField("notes", event.target.value)} className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" />
          </div>
          <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.isDefaultForRole} onChange={(event) => setField("isDefaultForRole", event.target.checked)} />
            <span>
              <span className="font-medium">Default preset for this role</span>
              <span className="mt-1 block text-xs text-neutral-500 dark:text-neutral-400">Recruitment suggests this preset first for the role.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.isActive} onChange={(event) => setField("isActive", event.target.checked)} />
            <span>
              <span className="font-medium">Active preset</span>
              <span className="mt-1 block text-xs text-neutral-500 dark:text-neutral-400">Inactive presets are kept for reference and no longer prefill pay.</span>
            </span>
          </label>
        </div>

    </Dialog>
  );
}

function TemplateDetailsModal({ template, canManage, onClose, onEdit, onDeactivate, onRestore, onDelete }) {
  useEffect(() => {
    if (!template || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, template]);

  if (!template || typeof document === "undefined") return null;

  const detailItems = [
    ["Business Role", template.business_role_display_name || template.business_role_name || "Unassigned role"],
    ["Department", template.department_name || "Unassigned department"],
    ["Employment Type", titleCase(template.employment_type)],
    ["Salary Frequency", titleCase(template.salary_frequency)],
    ["Currency", template.currency_code || "PHP"],
    ["Base Salary", formatOptionalCents(template.base_salary_cents)],
    ["Allowance", formatOptionalCents(template.allowance_cents)],
    ...(supportsFixedAllowance(template.salary_frequency)
      ? [...(template.salary_day_coverage ? [["Salary usually pays for", SALARY_COVERAGE_OPTIONS.find((option) => option.value === template.salary_day_coverage)?.label]] : []),
        ["Daily and hourly rate", "Set on each employee's pay record"]]
      : [["Daily Rate", formatOptionalCents(template.daily_rate_cents)], ["Hourly Rate", formatOptionalCents(template.hourly_rate_cents)]]),
    ["Effective From", formatDate(template.effective_from)],
    ["Effective Until", template.effective_until ? formatDate(template.effective_until) : "Open-ended"],
  ];

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex justify-end bg-black/55 backdrop-blur-sm" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside role="dialog" aria-modal="true" aria-labelledby="salary-template-details-title" className="flex h-full w-full max-w-xl flex-col border-l border-neutral-200 bg-white text-neutral-900 shadow-2xl dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-50">
        <header className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-5 dark:border-neutral-800">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">Pay preset</div>
            <h2 id="salary-template-details-title" className="mt-1 truncate text-xl font-semibold">{template.template_name || "Untitled preset"}</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {Number(template.is_default_for_role) === 1 ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Default</span> : null}
              {Number(template.is_system_seeded) === 1 ? <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">Seeded</span> : null}
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${Number(template.is_active) === 1 ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"}`}>{Number(template.is_active) === 1 ? "Active" : "Inactive"}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white" aria-label="Close pay preset details"><X className="h-5 w-5" /></button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            {detailItems.map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</div>
                <div className="mt-1.5 text-sm font-medium text-neutral-900 dark:text-neutral-100">{value}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Notes</div>
            <div className="mt-1.5 whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-200">{template.notes || "No notes provided."}</div>
          </div>
        </div>

        {canManage ? (
          <footer className="flex justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-4 dark:border-neutral-800 dark:bg-neutral-900/70">
            {/* APP-HR-080: an inactive preset can be made available again; any preset can be deleted, since it only prefills a form. */}
            <button type="button" onClick={onDelete} className="mr-auto inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 dark:border-rose-900/40 dark:text-rose-300">Delete</button>
            {Number(template.is_active) === 1
              ? <button type="button" onClick={onDeactivate} className="inline-flex items-center gap-2 rounded-xl border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 dark:border-neutral-700 dark:text-neutral-200"><Archive className="h-4 w-4" />Deactivate</button>
              : <button type="button" onClick={onRestore} className="inline-flex items-center gap-2 rounded-xl border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 dark:border-neutral-700 dark:text-neutral-200">Restore</button>}
            <button type="button" onClick={onEdit} className="business-primary-action inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 text-sm font-semibold text-white dark:bg-neutral-100 dark:text-neutral-900"><PencilLine className="h-4 w-4" />Edit preset</button>
          </footer>
        ) : null}
      </aside>
    </div>,
    document.body,
  );
}

function MissingRolesModal({ open, roles, onClose }) {
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }} title="Roles without a preset" description="Optional: create a preset if you want Recruitment to suggest a starting amount for this role." size="md" closeOnOutsideClick>
        <ul className="divide-y divide-subtle overflow-hidden rounded-panel border border-default" aria-label="Roles without an active pay preset">
          {roles.map((role) => (
            <li key={role.id} className="flex min-h-14 flex-col items-start justify-center gap-0.5 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <span className="min-w-0 truncate text-body-sm font-semibold text-foreground">{role.displayName || role.display_name || role.name}</span>
              <span className="shrink-0 text-meta text-secondary">No active pay preset</span>
            </li>
          ))}
        </ul>
    </Dialog>
  );
}

/**
 * Pay presets (owner decision C7). HR operates them from HR > Pay presets (`scope="hr"`, editable with permission
 * to manage employee pay); Finance > Pay presets is the same list, read-only.
 */
export default function FinanceSalaries({ scope = "finance" }) {
  const auth = useBusinessAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [hrSettings, setHrSettings] = useState({ work_days_per_week: 5, work_hours_per_day: 8 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsTemplate, setDetailsTemplate] = useState(null);
  const [missingRolesOpen, setMissingRolesOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState("");
  const [salaryPage, setSalaryPage] = useState(1);
  const [pageError, setPageError] = useState("");
  const [autoOpenHandled, setAutoOpenHandled] = useState(false);

  const authRole = String(auth?.role || auth?.user?.role || "").toUpperCase();
  const isHrScope = scope === "hr";
  const mayManagePay = Boolean(authRole === "OWNER" || auth?.hasPermission?.("hr:compensation:manage"));
  // Only the HR page edits; the server enforces the same permission.
  const canManageTemplates = isHrScope && mayManagePay;

  const load = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const [templatesRes, rolesRes, employmentTypesRes, hrSettingsRes] = await Promise.all([
        financeAPI.listSalaryTemplates(),
        businessApi.getHRRoles().catch(() => ({ data: [] })),
        businessApi.getEmploymentTypes().catch(() => ({ data: [] })),
        businessApi.getHRSettings().catch(() => ({})),
      ]);
      setTemplates(normalizeTemplatesResponse(templatesRes));
      setRoles(normalizeRolesResponse(rolesRes).filter((role) => String(role.name || "").toUpperCase() !== "OWNER"));
      setEmploymentTypes(normalizeEmploymentTypesResponse(employmentTypesRes));
      setHrSettings((current) => ({
        ...current,
        ...(hrSettingsRes?.data || {}),
        ...(hrSettingsRes || {}),
      }));
    } catch (error) {
      setTemplates([]);
      setRoles([]);
      setEmploymentTypes([]);
      setHrSettings({ work_days_per_week: 5, work_hours_per_day: 8 });
      setPageError(cleanActionError(error, "Pay presets could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => ({
    active: templates.filter((item) => Number(item.is_active) === 1).length,
    defaults: templates.filter((item) => Number(item.is_default_for_role) === 1 && Number(item.is_active) === 1).length,
    mappedRoles: new Set(templates.filter((item) => Number(item.is_active) === 1).map((item) => Number(item.business_role_id))).size,
    totalBaseCents: templates
      .filter((item) => Number(item.is_active) === 1)
      .reduce((sum, row) => sum + Number(row.base_salary_cents || 0), 0),
  }), [templates]);

  const rolesMissingTemplates = useMemo(
    () => roles.filter((role) => String(role.name || '').toUpperCase() !== 'OWNER' && Number(role.activeSalaryTemplateCount || 0) <= 0),
    [roles],
  );

  const employmentTypeFilterOptions = useMemo(() => {
    const values = new Set(templates.map((template) => String(template.employment_type || "")).filter(Boolean));
    return Array.from(values).sort().map((value) => ({ value, label: titleCase(value) }));
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return templates.filter((template) => {
      const matchesQuery = !normalizedQuery || [
        template.template_name,
        template.business_role_display_name,
        template.business_role_name,
        template.department_name,
        template.template_key,
      ].some((value) => String(value || "").toLowerCase().includes(normalizedQuery));
      const matchesRole = !roleFilter || String(template.business_role_id) === String(roleFilter);
      const matchesStatus = !statusFilter || (statusFilter === "active" ? Number(template.is_active) === 1 : Number(template.is_active) !== 1);
      const matchesEmploymentType = !employmentTypeFilter || String(template.employment_type) === employmentTypeFilter;
      return matchesQuery && matchesRole && matchesStatus && matchesEmploymentType;
    });
  }, [employmentTypeFilter, query, roleFilter, statusFilter, templates]);

  const salaryTotalPages = Math.max(1, Math.ceil(filteredTemplates.length / SALARY_ROWS_PER_PAGE));
  const paginatedTemplates = useMemo(() => {
    const start = (salaryPage - 1) * SALARY_ROWS_PER_PAGE;
    return filteredTemplates.slice(start, start + SALARY_ROWS_PER_PAGE);
  }, [filteredTemplates, salaryPage]);

  useEffect(() => {
    setSalaryPage(1);
  }, [employmentTypeFilter, query, roleFilter, statusFilter]);

  useEffect(() => {
    setSalaryPage((current) => Math.min(current, salaryTotalPages));
  }, [salaryTotalPages]);

  const openCreateTemplateModal = useCallback((role = null) => {
    if (!canManageTemplates) return;
    const roleName = role?.displayName || role?.display_name || role?.name || "Business Role";
    setEditing(role ? {
      business_role_id: role.id,
      business_role_display_name: roleName,
      template_name: `${roleName} Default Salary`,
      employment_type: "full_time",
      salary_frequency: "monthly",
      effective_from: TODAY,
      is_default_for_role: 1,
      is_active: 1,
    } : null);
    setModalOpen(true);
  }, [canManageTemplates]);

  useEffect(() => {
    if (autoOpenHandled || !canManageTemplates || !roles.length) return;
    const roleId = Number(searchParams.get("roleId"));
    if (!Number.isInteger(roleId) || roleId <= 0) {
      setAutoOpenHandled(true);
      return;
    }
    const targetRole = roles.find((role) => Number(role.id) === roleId);
    if (targetRole) {
      openCreateTemplateModal(targetRole);
    }
    setAutoOpenHandled(true);
    const next = new URLSearchParams(searchParams);
    next.delete("roleId");
    setSearchParams(next, { replace: true });
  }, [autoOpenHandled, canManageTemplates, openCreateTemplateModal, roles, searchParams, setSearchParams]);

  async function handleSave(data) {
    if (editing?.id) {
      await financeAPI.updateSalaryTemplate(editing.id, data);
    } else {
      await financeAPI.createSalaryTemplate(data);
    }
    setModalOpen(false);
    setEditing(null);
    await load();
  }

  // C2: a preset only prefills pay in Recruitment, so deactivating it changes nobody's saved pay. Confirmed in a
  // ConfirmDialog; a refusal stays in the dialog.
  const [deactivating, setDeactivating] = useState(null);
  function handleDeactivate(template) {
    if (!canManageTemplates) return;
    setDeactivating(template);
  }
  async function confirmDeactivate() {
    await financeAPI.deactivateSalaryTemplate(deactivating.id);
    setDeactivating(null);
    await load();
  }

  // APP-HR-080: restore an inactive preset, or delete one for good. Both are confirmed; a refusal stays in the dialog.
  const [lifecycle, setLifecycle] = useState(null); // { kind: "restore" | "delete", template }
  async function confirmLifecycle() {
    if (lifecycle.kind === "restore") await financeAPI.restoreSalaryTemplate(lifecycle.template.id);
    else await financeAPI.deleteSalaryTemplate(lifecycle.template.id);
    setLifecycle(null);
    await load();
  }

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={isHrScope ? "HR" : "Finance"}
        title="Pay presets"
        subtitle="Optional starting amounts per business role. A preset only prefills pay in Recruitment; it never changes anyone's saved pay."
      >
        {canManageTemplates ? (
          <button
            type="button"
            onClick={() => openCreateTemplateModal()}
            className="business-primary-action inline-flex items-center gap-2 rounded-2xl bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white dark:bg-neutral-100 dark:text-neutral-900"
          >
            <Plus className="h-4 w-4" />
            Add pay preset
          </button>
        ) : null}
      </PageHeader>

      <div className="grid overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900 sm:grid-cols-2 xl:grid-cols-5">
        {[
          ["Active presets", loading ? "-" : String(summary.active)],
          ["Role Defaults", loading ? "-" : String(summary.defaults)],
          ["Mapped Roles", loading ? "-" : String(summary.mappedRoles)],
          ["Roles without a preset", loading ? "-" : String(rolesMissingTemplates.length)],
          ["Active Base Total", loading ? "-" : formatCents(summary.totalBaseCents)],
        ].map(([label, value], index) => (
          <div key={label} className={`px-4 py-3 ${index ? "border-t border-neutral-200 dark:border-neutral-800 sm:border-l xl:border-t-0" : ""} ${index === 2 ? "sm:border-l-0 xl:border-l" : ""} ${index === 4 ? "sm:col-span-2 xl:col-span-1" : ""}`}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</div>
            <div className="mt-1 truncate text-lg font-semibold text-neutral-900 dark:text-neutral-100">{value}</div>
          </div>
        ))}
      </div>

      <Panel title="Presets by role" subtitle="Optional. A preset can prefill an employee's pay when they are hired through Recruitment; HR always confirms the actual pay.">
        {pageError ? (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/20 dark:text-rose-300">
            {pageError}
          </div>
        ) : null}
        {!canManageTemplates ? (
          <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/30 dark:bg-amber-950/20 dark:text-amber-300">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              {isHrScope
                ? "You can view pay presets. Adding or changing them needs permission to manage employee pay."
                : "Pay presets are set up by HR. This is a read-only view."}
              {!isHrScope && mayManagePay ? <>{" "}<Link to="/business/hr/pay-presets" className="font-semibold underline">Open HR &gt; Pay presets</Link></> : null}
            </div>
          </div>
        ) : null}
        {!loading && rolesMissingTemplates.length > 0 ? (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950/40 dark:text-neutral-300">
            <div className="flex min-w-0 items-center gap-2">
              <span>{rolesMissingTemplates.length} business role{rolesMissingTemplates.length === 1 ? " has" : "s have"} no pay preset. That is fine: HR enters each employee's pay when hiring.</span>
            </div>
            <button type="button" onClick={() => setMissingRolesOpen(true)} className="shrink-0 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-xs font-semibold text-neutral-800 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800">
              Review roles
            </button>
          </div>
        ) : null}
        {!loading && templates.length ? (
          <div className="mb-4 grid gap-2 lg:grid-cols-[minmax(240px,1fr)_220px_170px_210px]">
            <label className="relative block">
              <span className="sr-only">Search pay presets</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search presets, roles, or departments..." className="min-h-10 w-full rounded-2xl border border-neutral-200 bg-white py-2 pl-10 pr-3 text-sm text-neutral-900 outline-none transition focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:focus:border-neutral-500" />
            </label>
            <ShopSelect value={roleFilter} onValueChange={setRoleFilter} options={[{ value: "", label: "All roles" }, ...roles.map((role) => ({ value: String(role.id), label: role.displayName || role.display_name || role.name }))]} ariaLabel="Filter by role" align="start" minPanelWidth={220} triggerClassName="min-h-10 w-full rounded-2xl px-3 py-2 text-sm" panelClassName="py-1" />
            <ShopSelect value={statusFilter} onValueChange={setStatusFilter} options={[{ value: "", label: "All statuses" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} ariaLabel="Filter by status" align="start" minPanelWidth={170} triggerClassName="min-h-10 w-full rounded-2xl px-3 py-2 text-sm" panelClassName="py-1" />
            <ShopSelect value={employmentTypeFilter} onValueChange={setEmploymentTypeFilter} options={[{ value: "", label: "All employment types" }, ...employmentTypeFilterOptions]} ariaLabel="Filter by employment type" align="end" minPanelWidth={210} triggerClassName="min-h-10 w-full rounded-2xl px-3 py-2 text-sm" panelClassName="py-1" />
          </div>
        ) : null}
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading pay presets...</div>
        ) : !templates.length ? (
          <EmptyState
            icon={Wallet}
            title="No pay presets yet"
            description="A pay preset is optional. It suggests a starting amount for a role when HR hires someone through Recruitment."
            action={canManageTemplates ? (
              <button
                type="button"
                onClick={() => openCreateTemplateModal()}
                className="business-primary-action inline-flex items-center gap-2 rounded-2xl bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white dark:bg-neutral-100 dark:text-neutral-900"
              >
                <Plus className="h-4 w-4" />
                Create pay preset
              </button>
            ) : null}
          />
        ) : !filteredTemplates.length ? (
          <div className="rounded-2xl border border-dashed border-neutral-300 px-4 py-10 text-center text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">No pay presets match the selected filters.</div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 lg:flex lg:min-h-[calc(100dvh-36rem)] lg:flex-col">
            <div className="overflow-x-auto bg-white dark:bg-neutral-950 lg:flex-1">
            <table className="w-full min-w-[820px] table-fixed text-left text-sm">
              <thead className="bg-neutral-100 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:bg-neutral-900 dark:text-neutral-400">
                <tr>
                  <th className="w-[28%] px-4 py-3">Role / Preset</th>
                  <th className="w-[16%] px-4 py-3">Type / Frequency</th>
                  <th className="w-[13%] px-4 py-3">Base pay</th>
                  <th className="w-[12%] px-4 py-3">Allowance</th>
                  <th className="w-[14%] px-4 py-3">Effective</th>
                  <th className="w-[9%] px-4 py-3">Status</th>
                  <th className="w-[8%] px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white dark:divide-neutral-800 dark:bg-neutral-950">
                {paginatedTemplates.map((template) => (
                  <tr key={template.id} className="h-[68px] transition hover:bg-neutral-50 dark:hover:bg-neutral-900/60">
                    <td className="px-4 py-3">
                      <div className="truncate font-semibold text-neutral-900 dark:text-neutral-100">{template.business_role_display_name || template.business_role_name || "Unassigned role"}</div>
                      <div className="mt-0.5 truncate text-xs text-neutral-500 dark:text-neutral-400">{template.template_name || "Untitled preset"}{template.department_name ? ` - ${template.department_name}` : ""}</div>
                    </td>
                    <td className="px-4 py-3"><div className="text-neutral-800 dark:text-neutral-200">{titleCase(template.employment_type)}</div><div className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{titleCase(template.salary_frequency)}</div></td>
                    <td className="px-4 py-3 font-medium text-neutral-900 dark:text-neutral-100">{formatCents(template.base_salary_cents)}</td>
                    <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">{formatCents(template.allowance_cents)}</td>
                    <td className="px-4 py-3"><div className="text-neutral-800 dark:text-neutral-200">{formatDate(template.effective_from)}</div><div className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{template.effective_until ? `Until ${formatDate(template.effective_until)}` : "Open-ended"}</div></td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${Number(template.is_active) === 1 ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"}`}>{Number(template.is_active) === 1 ? "Active" : "Inactive"}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button type="button" onClick={() => setDetailsTemplate(template)} className="flex h-9 w-9 items-center justify-center rounded-xl text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-white" aria-label={`View ${template.template_name || "pay preset"}`}><Eye className="h-4 w-4" /></button>
                        {canManageTemplates ? <button type="button" onClick={() => { setEditing(template); setModalOpen(true); }} className="flex h-9 w-9 items-center justify-center rounded-xl text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-white" aria-label={`Edit ${template.template_name || "pay preset"}`}><PencilLine className="h-4 w-4" /></button> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="flex min-h-[4.25rem] flex-wrap items-center justify-between gap-2 border-t border-neutral-200 bg-neutral-50 px-4 dark:border-neutral-800 dark:bg-neutral-900/60 lg:shrink-0">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Showing {(salaryPage - 1) * SALARY_ROWS_PER_PAGE + 1}-{Math.min(salaryPage * SALARY_ROWS_PER_PAGE, filteredTemplates.length)} of {filteredTemplates.length}
              </span>
              <Pagination page={salaryPage} totalPages={salaryTotalPages} onPageChange={setSalaryPage} />
            </div>
          </div>
        )}
      </Panel>

      <TemplateDetailsModal template={detailsTemplate} canManage={canManageTemplates} onClose={() => setDetailsTemplate(null)} onEdit={() => { setEditing(detailsTemplate); setDetailsTemplate(null); setModalOpen(true); }} onDeactivate={async () => { const target = detailsTemplate; setDetailsTemplate(null); if (target) await handleDeactivate(target); }} onRestore={() => { const target = detailsTemplate; setDetailsTemplate(null); if (target && canManageTemplates) setLifecycle({ kind: "restore", template: target }); }} onDelete={() => { const target = detailsTemplate; setDetailsTemplate(null); if (target && canManageTemplates) setLifecycle({ kind: "delete", template: target }); }} />
      <MissingRolesModal open={missingRolesOpen} roles={rolesMissingTemplates} onClose={() => setMissingRolesOpen(false)} />

      <TemplateModal
        open={modalOpen}
        initial={editing}
        roles={roles}
        employmentTypes={employmentTypes}
        hrSettings={hrSettings}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
      />
      {lifecycle ? (
        <ConfirmDialog
          title={`${lifecycle.kind === "restore" ? "Restore" : "Delete"} the pay preset for ${lifecycle.template.business_role_display_name || lifecycle.template.business_role_name}?`}
          consequence={lifecycle.kind === "restore"
            ? "It prefills pay in Recruitment again. It does not become the default for its role, and nobody's saved pay changes."
            : "The preset is removed for good. This cannot be undone. A preset only prefills a form, so nobody's saved pay changes."}
          confirmLabel={lifecycle.kind === "restore" ? "Restore preset" : "Delete preset"}
          cancelLabel={lifecycle.kind === "restore" ? "Keep inactive" : "Keep preset"}
          tone={lifecycle.kind === "restore" ? "default" : "danger"}
          failureTitle={lifecycle.kind === "restore" ? "Preset not restored" : "Preset not deleted"}
          onConfirm={confirmLifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {deactivating ? (
        <ConfirmDialog
          title={`Deactivate the pay preset for ${deactivating.business_role_display_name || deactivating.business_role_name}?`}
          consequence="It stops prefilling pay in Recruitment. Nobody's saved pay changes."
          confirmLabel="Deactivate preset"
          cancelLabel="Keep preset"
          tone="danger"
          failureTitle="Preset not deactivated"
          onConfirm={confirmDeactivate}
          onClose={() => setDeactivating(null)}
        />
      ) : null}
    </div>
  );
}
